import AppBadge from '@/components/ui/AppBade';
import AppButton from '@/components/ui/AppButton';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest, axiosPatchRequest } from '@/services/Request';
import { getBookImageUrl, IBookData } from '@/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign, FileText, Tag, User,
  BookOpen, Calendar, MapPin, Layers, Hash, Package, ExternalLink,
  Check, XCircle, Clock, Pencil,
} from 'lucide-react';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useParams } from 'react-router';
import { Link } from 'react-router';
import avatar from '../../assets/images/blank-avatar-300x300.png';
import BookSkeleton from './BookSkeleton';
import EditBookModal from './EditBookModal';
import { useState } from 'react';
import { toast } from 'react-toastify';

interface BookDetailsProps {
  bookId?: string;
}

export default function BookDetails({ bookId }: BookDetailsProps) {
  const { id } = useParams();
  const resolvedId = id || bookId;
  const [selectedImage, setSelectedImage] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [endpoints.getSpecificBook, id, bookId],
    queryFn: () => axiosGetRequest(endpoints.getSpecificBook, { id: resolvedId }),
    select: (data): IBookData => data.result,
    enabled: !!resolvedId,
  });

  const { data: categories } = useQuery({
    queryKey: [endpoints.getAllCategories],
    queryFn: () => axiosGetRequest(endpoints.getAllCategories),
    select: data => data?.result,
  });

  const updateStatus = useMutation({
    mutationFn: (status: string) =>
      axiosPatchRequest(endpoints.updateBookStatus, { bookStatus: status }, { id: resolvedId ?? '' }),
    onSuccess: (_, status) => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificBook, id, bookId] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllBooks] });
      toast.success(`Book ${status} successfully`);
    },
    onError: () => toast.error('Failed to update book status'),
  });

  const getCategoryName = (category: any) => {
    if (!category) return '';
    if (typeof category === 'object') {
      return category.name_en || category.name_ar || category.categoryName || category.name || category._id || '';
    }
    const cat = categories?.find((c: any) => c._id === category);
    return cat?.name_en || cat?.name_ar || cat?.categoryName || cat?.name || category;
  };

  const currentStatus = data?.bookStatus;

  return (
    <>
      {isLoading ? (
        <BookSkeleton />
      ) : (
        <div className="rounded-xl border border-coal-200 bg-white">
          {(bookId || (id && !isLoading)) && (
            <div className="p-4 sm:p-5">

              {/* ── Status Action Bar ──────────────────────────────── */}
              <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-coal-200 bg-coal-50/50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-coal-500">Book Status:</span>
                  {currentStatus === 'pending' && (
                    <AppBadge size="small" variant="label" color="warning">Pending review</AppBadge>
                  )}
                  {currentStatus === 'approved' && (
                    <AppBadge size="small" variant="label" color="success">Approved</AppBadge>
                  )}
                  {currentStatus === 'rejected' && (
                    <AppBadge size="small" variant="label" color="error">Rejected</AppBadge>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <AppButton
                    theme="primary"
                    size="xs"
                    onClick={() => setShowEditModal(true)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </AppButton>
                  <AppButton
                    theme="success"
                    size="xs"
                    disabled={currentStatus === 'approved' || updateStatus.isPending}
                    loading={updateStatus.isPending && updateStatus.variables === 'approved'}
                    onClick={() => updateStatus.mutate('approved')}
                  >
                    <Check className="h-3.5 w-3.5" />
                    Approve
                  </AppButton>
                  <AppButton
                    theme="default"
                    size="xs"
                    disabled={currentStatus === 'pending' || updateStatus.isPending}
                    loading={updateStatus.isPending && updateStatus.variables === 'pending'}
                    onClick={() => updateStatus.mutate('pending')}
                  >
                    <Clock className="h-3.5 w-3.5" />
                    Pending
                  </AppButton>
                  <AppButton
                    theme="error"
                    size="xs"
                    disabled={currentStatus === 'rejected' || updateStatus.isPending}
                    loading={updateStatus.isPending && updateStatus.variables === 'rejected'}
                    onClick={() => updateStatus.mutate('rejected')}
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Reject
                  </AppButton>
                </div>
              </div>

              {/* ── Image Gallery ──────────────────────────────────── */}
              {data?.images && data.images.length > 0 && (
                <div className="mb-5">
                  <div className="overflow-hidden rounded-lg border border-coal-200">
                    <img
                      src={getBookImageUrl(data.images[selectedImage])}
                      className="h-48 w-full object-contain bg-coal-50 sm:h-56"
                      alt={data?.title}
                    />
                  </div>
                  {data.images.length > 1 && (
                    <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                      {data.images.map((image, i) => (
                        <button
                          key={image.key || image.url}
                          onClick={() => setSelectedImage(i)}
                          className={`h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                            selectedImage === i
                              ? 'border-primary ring-1 ring-primary/30'
                              : 'border-coal-200 hover:border-coal-400'
                          }`}
                        >
                          <img src={getBookImageUrl(image)} className="h-full w-full object-cover" alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Book Owner ─────────────────────────────────────── */}
              <div className="mb-5 rounded-lg border border-coal-200 bg-coal-50/50 p-3 sm:p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-coal-400">
                  Book Owner
                </h3>
                <div className="flex items-center gap-3">
                  <img src={avatar} alt="Owner" className="h-10 w-10 rounded-full ring-2 ring-coal-200" />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/users/profile/?uid=${data?.createdBy?._id}`}
                      className="group flex items-center gap-1 text-sm font-semibold text-coal-900 transition-colors hover:text-primary truncate"
                    >
                      {data?.createdBy?.fullName}
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </Link>
                    <p className="text-xs text-coal-400 truncate">
                      {data?.createdBy?.phoneNumber || data?.createdBy?.email || '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <AppBadge size="small" variant="label" color="info">{data?.createdBy?.role}</AppBadge>
                    {data?.createdBy?.isActive ? (
                      <AppBadge size="small" variant="label" color="success">Active</AppBadge>
                    ) : (
                      <AppBadge size="small" variant="label" color="error">Inactive</AppBadge>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Book Details ───────────────────────────────────── */}
              <div>
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-coal-400">
                  Book Details
                </h3>
                <div className="rounded-lg border border-coal-200 divide-y divide-coal-100">
                  <DetailRow icon={FileText} label="Title" value={data?.title} />
                  <DetailRow icon={FileText} label="Description" value={data?.description} />
                  <div className="flex items-center gap-3 px-4 py-2.5">
                    <DollarSign className="h-4 w-4 flex-shrink-0 text-coal-400" />
                    <span className="w-28 flex-shrink-0 text-xs text-coal-400 sm:w-32">Price</span>
                    <div>
                      {data?.price ? (
                        <AppBadge size="small" variant="label" color="primary">{data?.price} EGP</AppBadge>
                      ) : (
                        <AppBadge size="small" variant="label" color="success">Free</AppBadge>
                      )}
                    </div>
                  </div>
                  <DetailRow icon={Layers} label="Category" value={data?.category && getCategoryName(data?.category)} />
                  <DetailRow
                    icon={Layers}
                    label="Sub Category"
                    value={
                      data?.subCategory
                        ? (typeof data.subCategory === 'object'
                            ? (data.subCategory as any).subCategory || (data.subCategory as any).name_en || (data.subCategory as any).name_ar || ''
                            : data.subCategory)
                        : ''
                    }
                  />
                  <div className="flex items-center gap-3 px-4 py-2.5">
                    <Tag className="h-4 w-4 flex-shrink-0 text-coal-400" />
                    <span className="w-28 flex-shrink-0 text-xs text-coal-400 sm:w-32">Post type</span>
                    <div>
                      {data?.bookType === 'offered' ? (
                        <AppBadge size="small" variant="label" color="primary">{data?.bookType}</AppBadge>
                      ) : (
                        <AppBadge size="small" variant="label" color="warning">{data?.bookType}</AppBadge>
                      )}
                    </div>
                  </div>
                  <DetailRow icon={BookOpen} label="Condition" value={data?.bookCondition} />
                  <DetailRow icon={Calendar} label="Edition" value={data?.bookEdition?.toString()} />
                  <DetailRow icon={Hash} label="No. of Books" value={data?.numberOfBooks?.toString()} />
                  <DetailRow icon={Package} label="Education Term" value={data?.educationTerm} />
                  <DetailRow icon={Package} label="Education Type" value={data?.educationType} />
                  <DetailRow icon={MapPin} label="Location" value={data?.location} />
                  <DetailRow icon={MapPin} label="City" value={data?.city} />
                  <DetailRow icon={User} label="Views" value={data?.views?.toString()} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {resolvedId && (
        <EditBookModal
          bookId={resolvedId}
          book={data}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificBook, id, bookId] });
          }}
        />
      )}
    </>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <Icon className="h-4 w-4 flex-shrink-0 text-coal-400" />
      <span className="w-28 flex-shrink-0 text-xs text-coal-400 sm:w-32">{label}</span>
      <span className="text-sm font-medium text-coal-800">{value}</span>
    </div>
  );
}
